import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { createUser, getChildUserInfos, updateUser, updateUserBranding, uploadUserBrandingLogo } from "../../utils/users";
import { UserContext } from "../../App";
import { auth } from "../../index";
import styled from "styled-components";
import { useProviderOptions } from "../../store/actions/providerActions";
import { useCompanyOptions } from "../../utils/company";
import Select from "react-select";
import { FooterActionsContainer } from "../layout/Footer";
import { OutlineButton } from "../layout/OutlineButton";
import { vehicleOptions, vehicles } from "../../utils/vehicles";

let objectRights = {
  create: false,
  read: false,
  update: false,
  delete: false,
};

// Generate default allowed vehicles structure automatically
function getDefaultAllowedVehicles() {
  const defaultVehicles = {};
  Object.keys(vehicles).forEach(vehicleKey => {
    defaultVehicles[vehicleKey] = true;
  });
  return defaultVehicles;
}

let defaultRights = function () {
  return {
    folder: { ...objectRights },
    booking: {
      ...objectRights,
      deleteHoursBeforePickup: { forbidden: false, nbHours: 0 },
      updateHoursBeforePickup: { forbidden: false, nbHours: 0 },
      createBetween: { forbidden: false, start: "", end: "" },
      maxDistanceKm: { forbidden: false, maxKm: 0 },
      allowedVehicles: getDefaultAllowedVehicles(),
    },
    turnover: { ...objectRights },
    driver_price: { ...objectRights },
    client_price: { ...objectRights },
    customer: { ...objectRights },
    provider: { ...objectRights },
    company: { ...objectRights },
    driver: { ...objectRights },
    vehicle: { ...objectRights },
    pricing: { ...objectRights },
    event: { ...objectRights },
    estimate: { ...objectRights },
    invoice: { ...objectRights },
    agents: { ...objectRights },
    chatbot: { ...objectRights },
  };
};

let objectLabels = [
  { key: "folder", label: "Dossier" },
  { key: "booking", label: "Réservation" },
  { key: "client_price", label: "(Colonne) Prix Client" },
  { key: "driver_price", label: "(Colonne) Prix Chauffeur" },
  { key: "turnover", label: "(Page) Chiffres" },
  { key: "customer", label: "Passager" },
  { key: "provider", label: "Client" },
  { key: "company", label: "Compagnie" },
  { key: "driver", label: "Chauffeur" },
  { key: "vehicle", label: "Véhicule" },
  { key: "pricing", label: "Tarif" },
  { key: "event", label: "Événement" },
  { key: "estimate", label: "Devis" },
  { key: "invoice", label: "Facture" },
  { key: "agents", label: "Agents" },
  { key: "chatbot", label: "Chatbot" },
];

function generateRandomPassword() {
  const length = 12;
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}

const UserRightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  row-gap: 8px;
  column-gap: 12px;
  align-items: center;
  justify-items: center;

  & > div:nth-child(5n + 1) {
    justify-self: start;
  }
`;

function UserRights({ rights, setRights }) {
  let rightsKeys = ["create", "read", "update", "delete"];
  return (
    <UserRightsGrid>
      <div></div>
      <div>Créer</div>
      <div>Lire</div>
      <div>Modifier</div>
      <div>Supprimer</div>
      {objectLabels.map(({ key: object, label }, index) => {
        return (
          <>
            <div key={index + "-label"}>{label}</div>
            {rightsKeys.map((action, index) => {
              return (
                <div key={index + "-right-" + action}>
                  <input
                    type="checkbox"
                    checked={rights[object][action]}
                    onChange={(e) => {
                      setRights((old) => {
                        let newRights = { ...old };
                        newRights[object] = {
                          ...old[object],
                          [action]: e.target.checked,
                        };
                        return newRights;
                      });
                    }}
                    disabled={
                      [
                        "turnover",
                        "driver_price",
                        "client_price",
                        "chatbot",
                      ].indexOf(object) !== -1 && action !== "read"
                    }
                  />
                </div>
              );
            })}
          </>
        );
      })}
    </UserRightsGrid>
  );
}

export default function UserAddContent() {
  const history = useHistory();
  const params = useParams();
  const paramUserId = params.userId;

  const { userInfos } = useContext(UserContext);

  const [isFetching, setIsFetching] = useState(false);
  const [errors, setErrors] = useState([]);
  const [form, setForm] = useState({
    email: "",
    password: generateRandomPassword(),
    isExternal: false,
  });

  const [branding, setBranding] = useState({ primaryColor: "#000000", displayName: "", email: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

  const [userRights, setUserRights] = useState(defaultRights());

  const providerOptions = useProviderOptions(true, "Tous les clients");
  const companyOptions = useCompanyOptions();

  useEffect(() => {
    if (paramUserId) {
      async function fetchUserInfos() {
        try {
          const user = await getChildUserInfos(userInfos.jwt, paramUserId);
          setForm({
            email: user.email,
            password: "",
            isExternal: !!user.isExternal,
          });

          setBranding({
            primaryColor: user?.branding?.primaryColor || "#000000",
            displayName: user?.branding?.displayName || "",
            email: user?.branding?.email || "",
          });

          setLogoPreviewUrl(user?.branding?.logoUrl);

          setUserRights(() => {
            const defaults = defaultRights();

            return {
              ...defaults,
              ...user.rights,
              booking : {
                ...defaults.booking,
                ...user.rights?.booking,
              }
            };
          });
        } catch (error) {
          console.error(error);
          setErrors([error.message]);
        }
      }

      fetchUserInfos().then();
    }
  }, [paramUserId, userInfos.jwt]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((old) => ({
      ...old,
      [name]: value,
    }));
  }

  async function submitCreateUser() {
    const user = await createUser(userInfos.jwt, form, userRights);
    // Save branding if external
    if (form.isExternal) {
      try {
        await updateUserBranding(userInfos.jwt, user.uid, {
          ...branding
        });
        if (logoFile) {
          await uploadUserBrandingLogo(userInfos.jwt, user.uid, logoFile);
        }
      } catch (e) {
        console.error("Branding save failed:", e);
        // Continue even if branding fails
      }
    }
    await auth.sendPasswordResetEmail(user.email);
    history.push("/users");
  }

  async function submitUpdateUser() {
    await updateUser(userInfos.jwt, paramUserId, form.isExternal, userRights);
    // Save branding if external
    if (form.isExternal) {
      try {
        await updateUserBranding(userInfos.jwt, paramUserId, {
          ...branding
        });
        if (logoFile) {
          await uploadUserBrandingLogo(userInfos.jwt, paramUserId, logoFile);
        }
      } catch (e) {
        console.error("Branding save failed:", e);
      }
    }
    window.alert("Utilisateur modifié avec succès");
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    if (handleValidation()) {
      try {
        setIsFetching(true);
        if (!paramUserId) {
          await submitCreateUser();
        } else {
          await submitUpdateUser();
        }
        setIsFetching(false);
      } catch (error) {
        console.error(error);
        setIsFetching(false);
        setErrors([error.message]);
      }
    }
  }

  function handleValidation() {
    let valid = true;
    let message = [];

    if (!form.email) {
      valid = false;
      message.push("Veuillez entrer un email.");
    }

    if (!paramUserId && !form.password) {
      valid = false;
      message.push("Veuillez entrer un mot de passe.");
    }

    setErrors(message);
    return valid;
  }

  const FormAlert = () => {
    return errors.length > 0 ? (
      <div
        className="alert alert-danger"
        role="alert"
        style={{ flexDirection: "column" }}
      >
        {errors.map((error, index) => (
          <div
            className="alert-text"
            key={index}
            style={{ alignSelf: "flex-start" }}
          >
            {error}
          </div>
        ))}
      </div>
    ) : null;
  };

  const buttonText = isFetching ? (
    <button type="submit" className="btn btn-primary px-5" disabled>
      {paramUserId ? "Modification..." : "Création..."}
    </button>
  ) : (
    <button type="submit" className="btn btn-primary px-5">
      {paramUserId ? "Modifier l'utilisateur" : "Créer l'utilisateur"}
    </button>
  );

  return (
    <div
      className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">
              {paramUserId ? "Modifier" : "Créer"} un utilisateur
            </h3>
          </div>
        </div>
      </div>

      <FormAlert />

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <div className="kt-portlet kt-portlet--tabs">
          <div className="kt-portlet__head">
            <div className="kt-portlet__head-label">
              <h3 className="kt-portlet__head-title">
                Données de l'utilisateur
              </h3>
            </div>
          </div>
          <form onSubmit={handleFormSubmit} className="kt-form">
            <div className="kt-portlet__body">
              <div className="kt-section kt-section--first">
                <div className="kt-section__body">
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">Email:</label>
                    <div className="col-lg-6">
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        disabled={paramUserId}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Mot de passe:
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        className="form-control"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        disabled={paramUserId}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Utilisateur externe:
                    </label>
                    <div className="col-lg-6 d-flex align-items-center">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="externalCheckbox"
                          checked={!!form.isExternal}
                          onChange={(e) =>
                            setForm((old) => ({
                              ...old,
                              isExternal: e.target.checked,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  {form.isExternal && (
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header bg-white">
                        <strong>Branding</strong>
                      </div>
                      <div className="card-body">
                        <div className="form-group row">
                          <label className="col-lg-3 col-form-label">Adresse mail:</label>
                          <div className="col-lg-6">
                            <input
                              type="email"
                              className="form-control"
                              value={branding.email || ""}
                              onChange={(e) =>
                                setBranding((old) => ({
                                  ...old,
                                  email: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-lg-3 col-form-label">Raison sociale:</label>
                          <div className="col-lg-6">
                            <input
                              type="text"
                              className="form-control"
                              value={branding.displayName || ""}
                              onChange={(e) =>
                                setBranding((old) => ({
                                  ...old,
                                  displayName: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-lg-3 col-form-label">Couleur principale:</label>
                          <div className="col-lg-6">
                            <input
                              type="color"
                              className="form-control"
                              value={branding.primaryColor || "#000000"}
                              onChange={(e) =>
                                setBranding((old) => ({
                                  ...old,
                                  primaryColor: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-lg-3 col-form-label">Logo:</label>
                          <div className="col-lg-6">
                            {logoPreviewUrl && (
                              <div className="mb-3 p-2 border rounded d-inline-block bg-light">
                                <img
                                  src={logoPreviewUrl}
                                  alt="Logo utilisateur"
                                  style={{ maxHeight: 80, maxWidth: "100%", objectFit: "contain" }}
                                />
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                setLogoFile(file);
                                try {
                                  if (file) {
                                    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
                                    const url = URL.createObjectURL(file);
                                    setLogoPreviewUrl(url);
                                  }
                                } catch (_) {}
                              }}
                            />
                            <small className="text-sm text-muted">
                              Le logo sera stocké et remplacé en cas de nouvel envoi.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">Droits:</label>
                    <div className="col-lg-6">
                      <UserRights
                        rights={userRights}
                        setRights={setUserRights}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3">
                      Restrictions Réservation:
                    </label>
                    <div className="col-lg-6">
                      <div className="row mb-2">
                        <div className="col-6">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="enforceForbidDeleteHoursBeforePickup"
                              checked={!!userRights.booking?.deleteHoursBeforePickup?.forbidden}
                              onChange={(e) =>
                                setUserRights((old) => ({
                                  ...old,
                                  booking: {
                                    ...old.booking,
                                    deleteHoursBeforePickup: {
                                      ...old.booking?.deleteHoursBeforePickup,
                                      forbidden: e.target.checked,
                                    },
                                  },
                                }))
                              }
                            />
                            <label className="form-check-label" htmlFor="enforceForbidDeleteHoursBeforePickup">
                              Interdire la suppression N heures avant le départ
                            </label>
                          </div>
                        </div>
                        <div className="col-6">
                          <input
                            type="number"
                            min={0}
                            className="form-control"
                            disabled={!userRights.booking?.deleteHoursBeforePickup?.forbidden}
                            value={userRights.booking?.deleteHoursBeforePickup?.nbHours ?? 0}
                            onChange={(e) =>
                              setUserRights((old) => ({
                                ...old,
                                booking: {
                                  ...old.booking,
                                  deleteHoursBeforePickup: {
                                    ...old.booking?.deleteHoursBeforePickup,
                                    nbHours: Math.max(0, parseInt(e.target.value || "0", 10)),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-6">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="enforceForbidUpdateHoursBeforePickup"
                              checked={!!userRights.booking?.updateHoursBeforePickup?.forbidden}
                              onChange={(e) =>
                                setUserRights((old) => ({
                                  ...old,
                                  booking: {
                                    ...old.booking,
                                    updateHoursBeforePickup: {
                                      ...old.booking?.updateHoursBeforePickup,
                                      forbidden: e.target.checked,
                                    },
                                  },
                                }))
                              }
                            />
                            <label className="form-check-label" htmlFor="enforceForbidUpdateHoursBeforePickup">
                              Interdire la modification N heures avant le départ
                            </label>
                          </div>
                        </div>
                        <div className="col-6">
                          <input
                            type="number"
                            min={0}
                            className="form-control"
                            disabled={!userRights.booking?.updateHoursBeforePickup?.forbidden}
                            value={userRights.booking?.updateHoursBeforePickup?.nbHours ?? 0}
                            onChange={(e) =>
                              setUserRights((old) => ({
                                ...old,
                                booking: {
                                  ...old.booking,
                                  updateHoursBeforePickup: {
                                    ...old.booking?.updateHoursBeforePickup,
                                    nbHours: Math.max(0, parseInt(e.target.value || "0", 10)),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-6">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="enforceForbidCreateBetween"
                              checked={!!userRights.booking?.createBetween?.forbidden}
                              onChange={(e) =>
                                setUserRights((old) => ({
                                  ...old,
                                  booking: {
                                    ...old.booking,
                                    createBetween: {
                                      ...old.booking?.createBetween,
                                      forbidden: e.target.checked,
                                    },
                                  },
                                }))
                              }
                            />
                            <label className="form-check-label" htmlFor="enforceForbidCreateBetween">
                              Interdire la création entre
                            </label>
                          </div>
                        </div>
                        <div className="col-3">
                          <input
                            type="time"
                            className="form-control"
                            disabled={!userRights.booking?.createBetween?.forbidden}
                            value={userRights.booking?.createBetween?.start || ""}
                            onChange={(e) =>
                              setUserRights((old) => ({
                                ...old,
                                booking: {
                                  ...old.booking,
                                  createBetween: {
                                    ...old.booking?.createBetween,
                                    start: e.target.value,
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="col-3">
                          <input
                            type="time"
                            className="form-control"
                            disabled={!userRights.booking?.createBetween?.forbidden}
                            value={userRights.booking?.createBetween?.end || ""}
                            onChange={(e) =>
                              setUserRights((old) => ({
                                ...old,
                                booking: {
                                  ...old.booking,
                                  createBetween: {
                                    ...old.booking?.createBetween,
                                    end: e.target.value,
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-6">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="enforceMaxDistanceKm"
                              checked={!!userRights.booking?.maxDistanceKm?.forbidden}
                              onChange={(e) =>
                                setUserRights((old) => ({
                                  ...old,
                                  booking: {
                                    ...old.booking,
                                    maxDistanceKm: {
                                      ...old.booking?.maxDistanceKm,
                                      forbidden: e.target.checked,
                                    },
                                  },
                                }))
                              }
                            />
                            <label className="form-check-label" htmlFor="enforceMaxDistanceKm">
                              Limiter la distance maximale (km)
                            </label>
                          </div>
                        </div>
                        <div className="col-6">
                          <input
                            type="number"
                            min={0}
                            step={0.1}
                            className="form-control"
                            disabled={!userRights.booking?.maxDistanceKm?.forbidden}
                            value={userRights.booking?.maxDistanceKm?.maxKm ?? 0}
                            onChange={(e) =>
                              setUserRights((old) => ({
                                ...old,
                                booking: {
                                  ...old.booking,
                                  maxDistanceKm: {
                                    ...old.booking?.maxDistanceKm,
                                    maxKm: Math.max(0, parseFloat(e.target.value || "0")),
                                  },
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-12">
                          <label className="form-label">
                            Véhicules autorisés:
                          </label>
                          <div className="row">
                            {vehicleOptions.map((vehicle) => (
                              <div key={vehicle.value} className="col-md-4 col-sm-6 mb-2">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`vehicle-${vehicle.value}`}
                                    checked={!!userRights.booking?.allowedVehicles?.[vehicle.value]}
                                    onChange={(e) =>
                                      setUserRights((old) => ({
                                        ...old,
                                        booking: {
                                          ...old.booking,
                                          allowedVehicles: {
                                            ...old.booking?.allowedVehicles,
                                            [vehicle.value]: e.target.checked,
                                          },
                                        },
                                      }))
                                    }
                                  />
                                  <label className="form-check-label" htmlFor={`vehicle-${vehicle.value}`}>
                                    {vehicle.label}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                          <small className="form-text text-muted">
                            Par défaut, tous les véhicules sont autorisés. Décochez les véhicules que cet utilisateur ne doit pas voir.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Restriction accès clients:
                    </label>
                    <div className="col-lg-6">
                      <Select
                        options={providerOptions}
                        isMulti
                        isClearable
                        isSearchable
                        value={providerOptions.filter((option) =>
                          userRights?.providersFilter?.find(
                            (_p) => _p === option.value
                          )
                        )}
                        onChange={(selectedOptions) => {
                          setUserRights((old) => {
                            if (
                              selectedOptions?.find(
                                (option) => option.value === null
                              )
                            ) {
                              return {
                                ...old,
                                providersFilter: [],
                              };
                            }

                            return {
                              ...old,
                              providersFilter:
                                selectedOptions?.map(
                                  (option) => option.value ?? -1
                                ) ?? [],
                            };
                          });
                        }}
                      />
                      <small className="text-sm text-muted">
                        Aucune sélection : aucun filtre
                      </small>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-lg-3 col-form-label">
                      Restriction accès sociétés (partenaire):
                    </label>
                    <div className="col-lg-6">
                      <Select
                        options={companyOptions}
                        isMulti
                        isClearable
                        isSearchable
                        value={companyOptions.filter((option) =>
                          userRights?.companyFilter?.find(
                            (_c) => _c === option.value
                          )
                        )}
                        onChange={(selectedOptions) => {
                          setUserRights((old) => ({
                            ...old,
                            companyFilter:
                              selectedOptions?.map((option) => option.value) ??
                              [],
                          }));
                        }}
                      />
                      <small className="text-sm text-muted">
                        Aucune sélection : aucun filtre. Sinon, l'utilisateur ne
                        voit que les courses attribuées à ces sociétés.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FooterActionsContainer>
              <div>
                <OutlineButton type="button" onClick={history.goBack}>
                  <i className="fas fa-arrow-left" />
                  Retour
                </OutlineButton>
              </div>
              <div>{buttonText}</div>
              <div></div>
            </FooterActionsContainer>
          </form>
        </div>
      </div>
    </div>
  );
}
